import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, FileStack, Clock, Users, Trash2, Edit, Copy, MapPin } from 'lucide-react';

function TemplateFormDialog({ open, onClose, template, services, onSuccess }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    service_id: '',
    shift_type: 'STANDARD',
    start_time: '09:00',
    end_time: '17:00',
    break_duration: 0,
    required_staff: 1,
    location: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        description: template.description || '',
        service_id: template.service_id || '',
        shift_type: template.shift_type || 'STANDARD',
        start_time: template.start_time || '09:00',
        end_time: template.end_time || '17:00',
        break_duration: template.break_duration || 0,
        required_staff: template.required_staff || 1,
        location: template.location || '',
        notes: template.notes || '',
      });
    } else {
      setForm({
        name: '',
        description: '',
        service_id: '',
        shift_type: 'STANDARD',
        start_time: '09:00',
        end_time: '17:00',
        break_duration: 0,
        required_staff: 1,
        location: '',
        notes: '',
      });
    }
  }, [template, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.service_id || !form.start_time || !form.end_time) {
      toast.error('Please fill required fields');
      return;
    }
    setLoading(true);
    try {
      if (template) {
        await api.put(`/roster/templates/${template.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/roster/templates', form);
        toast.success('Template created');
      }
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileStack className="w-5 h-5 text-purple-500" />
            {template ? 'Edit Template' : 'Create Template'}
          </DialogTitle>
          <DialogDescription>
            Shift templates save time by pre-filling common shift details
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Template Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Morning Personal Care"
              data-testid="template-name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service *</Label>
              <LineItemSelect items={services} showRate={false} value={form.service_id} onChange={(v) => setForm({ ...form, service_id: v })}
                  placeholder="Select service" testId="template-service" />
            </div>
            <div>
              <Label>Shift Type</Label>
              <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="SIL">SIL</SelectItem>
                  <SelectItem value="SLEEPOVER">Sleepover</SelectItem>
                  <SelectItem value="ACTIVE_NIGHT">Active Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label>Start Time *</Label>
              <TrinitiTimePicker
                value={form.start_time}
                onChange={(v) => setForm({ ...form, start_time: v })}
                testId="template-start-time"
              />
            </div>
            <div>
              <Label>End Time *</Label>
              <TrinitiTimePicker
                value={form.end_time}
                onChange={(v) => setForm({ ...form, end_time: v })}
                testId="template-end-time"
              />
            </div>
            <div>
              <Label>Break (min)</Label>
              <Input
                type="number"
                min={0}
                value={form.break_duration}
                onChange={(e) => setForm({ ...form, break_duration: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Required Staff</Label>
              <Input
                type="number"
                min={1}
                value={form.required_staff}
                onChange={(e) => setForm({ ...form, required_staff: parseInt(e.target.value) || 1 })}
                data-testid="template-required-staff"
              />
            </div>
          </div>

          <div>
            <Label>Location</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Optional location"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes for shifts created from this template"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="template-submit">
              {loading ? 'Saving...' : template ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UseTemplateDialog({ template, open, onClose, participants, houses, onSuccess }) {
  const [shiftDate, setShiftDate] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [houseId, setHouseId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!shiftDate) {
      toast.error('Please select a date');
      return;
    }
    setLoading(true);
    try {
      let url = `/roster/templates/${template.id}/create-shift?shift_date=${shiftDate}`;
      if (participantId) url += `&participant_id=${participantId}`;
      if (houseId) url += `&house_id=${houseId}`;
      await api.post(url);
      toast.success('Shift created from template');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Shift from Template</DialogTitle>
          <DialogDescription>Use "{template?.name}" to quickly create a shift</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Shift Date *</Label>
            <TrinitiDatePicker
              value={shiftDate}
              onChange={(v) => setShiftDate(v)}
              testId="use-template-date"
            />
          </div>
          <div>
            <Label>Participant (Optional)</Label>
            <AsyncSearchSelect
              endpoint="/participants"
              value={participantId}
              onChange={(v) => setParticipantId(v)}
              getOptionLabel={participantLabel}
              selectedLabel={labelFor(participants, participantId, participantLabel)}
              placeholder="Select participant"
              searchPlaceholder={PARTICIPANT_SEARCH_HINT}
              emptyText="No participants match that search"
              clearLabel="None"
              testId="use-template-participant"
            />
          </div>
          <div>
            <Label>House (Optional)</Label>
            <Select value={houseId || "none"} onValueChange={(v) => setHouseId(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select house" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {houses.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} data-testid="use-template-submit">
            {loading ? 'Creating...' : 'Create Shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ShiftTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [houses, setHouses] = useState([]);
  const [formDialog, setFormDialog] = useState({ open: false, template: null });
  const [useDialog, setUseDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, servicesRes, participantsRes, housesRes] = await Promise.all([
        api.get('/roster/templates'),
        api.get('/services/catalog?mode=selection'),
        api.get('/participants'),
        api.get('/houses').catch(() => ({ data: { data: [] } })),
      ]);
      setTemplates(templatesRes.data.data || []);
      setServices(servicesRes.data.data || []);
      setParticipants(participantsRes.data.data || []);
      setHouses(housesRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/roster/templates/${deleteConfirm.id}`);
      toast.success('Template archived');
      setDeleteConfirm(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive template');
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service?.name || 'Unknown Service';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shift-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileStack className="w-6 h-6 text-purple-500" />
            Shift Templates
          </h1>
          <p className="text-muted-foreground mt-1">Save time with reusable shift templates</p>
        </div>
        <Button onClick={() => setFormDialog({ open: true, template: null })} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileStack className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No shift templates found</p>
            <p className="text-sm text-muted-foreground mt-1">Create templates for common shift types</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{t.name}</h3>
                    {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
                  </div>
                  <Badge variant="outline">{t.shift_type}</Badge>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{t.start_time} - {t.end_time}</span>
                    {t.break_duration > 0 && <span className="text-xs">({t.break_duration}min break)</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{t.required_staff} staff required</span>
                  </div>
                  {t.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{t.location}</span>
                    </div>
                  )}
                  <div className="pt-1">
                    <Badge variant="secondary" className="text-xs">{getServiceName(t.service_id)}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => setUseDialog(t)}
                    data-testid={`use-template-${t.id}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Use Template
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFormDialog({ open: true, template: t })}
                    data-testid={`edit-template-${t.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteConfirm(t)}
                    data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateFormDialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, template: null })}
        template={formDialog.template}
        services={services}
        onSuccess={fetchData}
      />

      {useDialog && (
        <UseTemplateDialog
          template={useDialog}
          open={!!useDialog}
          onClose={() => setUseDialog(null)}
          participants={participants}
          houses={houses}
          onSuccess={fetchData}
        />
      )}

      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
        title="Archive Template"
        description={`Are you sure you want to archive "${deleteConfirm?.name}"? It will no longer appear in the list.`}
        confirmLabel="Archive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
